import { completePathname, completeHash, completeQuery, stripBaseSegment } from '@hickory/location-utils';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function defaultParseQuery(query) {
    return query ? query : "";
}
function defaultStringifyQuery(query) {
    return query ? query : "";
}
function isValidBase(baseSegment) {
    return (typeof baseSegment === "string" &&
        baseSegment.charAt(0) === "/" &&
        baseSegment.charAt(baseSegment.length - 1) !== "/");
}
function locationFactory(options) {
    if (options === void 0) { options = {}; }
    var _a = options.query, _b = _a === void 0 ? {} : _a, _c = _b.parse, parseQuery = _c === void 0 ? defaultParseQuery : _c, _d = _b.stringify, stringifyQuery = _d === void 0 ? defaultStringifyQuery : _d, _e = options.decode, decode = _e === void 0 ? true : _e, _f = options.baseSegment, baseSegment = _f === void 0 ? "" : _f, _g = options.raw, raw = _g === void 0 ? function (p) { return p; } : _g;
    if (baseSegment !== "" && !isValidBase(baseSegment)) {
        throw new Error('The baseSegment "' +
            baseSegment +
            '" is not valid.' +
            " The baseSegment must begin with a forward slash and end with a" +
            " non-forward slash character.");
    }
    function parsePath(value) {
        var location = {};
        // hash is always after query, so split it off first
        var hashIndex = value.indexOf("#");
        if (hashIndex !== -1) {
            location.hash = value.substring(hashIndex + 1);
            value = value.substring(0, hashIndex);
        }
        else {
            location.hash = "";
        }
        var queryIndex = value.indexOf("?");
        if (queryIndex !== -1) {
            location.query = parseQuery(value.substring(queryIndex + 1));
            value = value.substring(0, queryIndex);
        }
        else {
            location.query = parseQuery();
        }
        location.pathname = stripBaseSegment(value, baseSegment);
        return location;
    }
    return {
        createLocation: function (value, key, state) {
            var partial;
            if (state === undefined) {
                state = null;
            }
            if (typeof value === "string") {
                partial = parsePath(value);
            }
            else {
                partial = __assign({}, value);
                if (partial.hash == null) {
                    partial.hash = "";
                }
                if (partial.query == null) {
                    partial.query = parseQuery();
                }
                if (partial.pathname == null) {
                    partial.pathname = "/";
                }
            }
            // don't set state if it already exists
            if (state && !partial.state) {
                partial.state = state;
            }
            var location = __assign({}, partial, { key: key, rawPathname: raw(partial.pathname) });
            // it can be more convenient to interact with the decoded pathname,
            // but leave the option for using the encoded value
            if (decode) {
                try {
                    location.pathname = decodeURI(location.pathname);
                }
                catch (e) {
                    throw new URIError('Pathname "' +
                        location.pathname +
                        '" could not be decoded. ' +
                        "This is most likely due to a bad percent-encoding. For more information, " +
                        "see the third paragraph here https://tools.ietf.org/html/rfc3986#section-2.4");
                }
            }
            return location;
        },
        createPath: function (location) {
            // ensure that pathname begins with a forward slash, query begins
            // with a question mark, and hash begins with a pound sign
            return (baseSegment +
                completePathname(location.rawPathname || location.pathname || "") +
                completeQuery(stringifyQuery(location.query)) +
                completeHash(location.hash));
        }
    };
}

function noop() { }
function createNavigationConfirmation() {
    var confirmFunction;
    return {
        confirmNavigation: function (info, confirm, prevent) {
            if (!confirmFunction) {
                confirm();
            }
            else {
                confirmFunction(info, confirm, prevent || noop);
            }
        },
        confirmWith: function (fn) {
            if (typeof fn !== "function") {
                throw new Error("confirmWith can only be passed a function");
            }
            confirmFunction = fn;
        },
        removeConfirmation: function () {
            confirmFunction = null;
        }
    };
}

function parse(key) {
    return key.split(".").map(function (value) { return parseInt(value, 10); });
}
function diff(first, second) {
    return parse(second)[0] - parse(first)[0];
}
function minor(current) {
    var _a = parse(current), major = _a[0], minor = _a[1];
    return major + "." + (minor + 1);
}
function createKeyGenerator() {
    var currentMajor = 0;
    return {
        keygen: {
            major: function (previous) {
                if (previous) {
                    currentMajor = parse(previous)[0] + 1;
                }
                return currentMajor++ + ".0";
            },
            minor: minor,
            diff: diff
        }
    };
}

function Common(options) {
    return __assign({}, locationFactory(options), createNavigationConfirmation(), createKeyGenerator());
}

export default Common;



//////////////////
// WEBPACK FOOTER
// ./node_modules/@hickory/root/dist/hickory-root.es.js
// module id = 254
// module chunks = 3